import React from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { Loader2, AlertCircle } from 'lucide-react';

const AreaChartComponent = ({
  data = [],
  areas = [{ key: 'value', name: 'Value', color: '#0052FF' }],
  xAxisKey = 'name',
  stacked = false,
  height = 300,
  loading = false,
  error = null,
  title = '',
  formatter = (v) => v,
}) => {
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center bg-white border border-slate-200 rounded-xl p-6" style={{ height }}>
        <Loader2 className="w-8 h-8 animate-spin text-[#0052FF] mb-2" />
        <span className="text-xs text-slate-400 font-semibold uppercase tracking-wider">Loading Chart...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center bg-white border border-slate-200 rounded-xl p-6 text-center" style={{ height }}>
        <AlertCircle className="w-8 h-8 text-amber-500 mb-2" />
        <span className="text-xs text-slate-600 font-medium">{error}</span>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center bg-white border border-slate-200 rounded-xl p-6 text-center" style={{ height }}>
        <span className="text-xs text-slate-400 font-semibold uppercase tracking-wider">No Data Available</span>
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-3" data-testid="area-chart-card">
      {title && <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider">{title}</h4>}
      <div style={{ width: '100%', height }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
            <defs>
              {areas.map((a, idx) => (
                <linearGradient key={a.key} id={`areaGrad_${a.key}_${idx}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={a.color || '#0052FF'} stopOpacity={0.4} />
                  <stop offset="95%" stopColor={a.color || '#0052FF'} stopOpacity={0.0} />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
            <XAxis dataKey={xAxisKey} tick={{ fill: '#64748B', fontSize: 11 }} axisLine={{ stroke: '#E2E8F0' }} />
            <YAxis tickFormatter={formatter} tick={{ fill: '#64748B', fontSize: 11 }} axisLine={{ stroke: '#E2E8F0' }} />
            <Tooltip
              contentStyle={{ backgroundColor: '#0F172A', borderColor: '#334155', borderRadius: '8px', color: '#FFF', fontSize: '12px' }}
              formatter={(value, name) => [formatter(value), name]}
            />
            <Legend wrapperStyle={{ fontSize: '11px', paddingTop: '8px' }} />
            {areas.map((a, idx) => (
              <Area
                key={a.key}
                type="monotone"
                dataKey={a.key}
                name={a.name || a.key}
                stroke={a.color || '#0052FF'}
                strokeWidth={2}
                fillOpacity={1}
                fill={`url(#areaGrad_${a.key}_${idx})`}
                stackId={stacked ? 'a' : undefined}
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default AreaChartComponent;
